/**
 * Hub URL pattern: /guides/{guideSlug}-world-cup-itinerary (one page per host; 3/5/7-day tabs).
 * Aligns with PREBUILT_ITINERARIES keys and stadiumCityToGuideSlug guideSlug values.
 */

export const ITINERARY_HUB_SUFFIX = '-world-cup-itinerary';

/** Full-bleed hero per host (Unsplash). */
export const ITINERARY_HUB_HERO_BY_SLUG = {
  'new-york': {
    src: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?auto=format&fit=crop&w=2000&q=80',
    alt: 'New York City skyline at dusk',
  },
  'los-angeles': {
    src: 'https://images.unsplash.com/photo-1580655653885-65763b2597d0?auto=format&fit=crop&w=2000&q=80',
    alt: 'Los Angeles downtown skyline',
  },
  dallas: {
    src: 'https://images.unsplash.com/photo-1581420580773-2599e720c667?auto=format&fit=crop&w=2000&q=80',
    alt: 'Dallas skyline',
  },
  atlanta: {
    src: 'https://images.unsplash.com/photo-1575917649708-95f076a2e7a2?auto=format&fit=crop&w=2000&q=80',
    alt: 'Atlanta skyline',
  },
  seattle: {
    src: 'https://images.unsplash.com/photo-1502175353174-a7a70e73bca7?auto=format&fit=crop&w=2000&q=80',
    alt: 'Seattle skyline with Space Needle',
  },
  'san-francisco': {
    src: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?auto=format&fit=crop&w=2000&q=80',
    alt: 'Golden Gate Bridge and San Francisco Bay',
  },
  miami: {
    src: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&w=2000&q=80',
    alt: 'Miami beach and oceanfront',
  },
  houston: {
    src: 'https://images.unsplash.com/photo-1549924190-f0057ef80efc?auto=format&fit=crop&w=2000&q=80',
    alt: 'Houston downtown',
  },
  philadelphia: {
    src: 'https://images.unsplash.com/photo-1560007544-ea13abe12e43?auto=format&fit=crop&w=2000&q=80',
    alt: 'Philadelphia skyline',
  },
  boston: {
    src: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=2000&q=80',
    alt: 'Boston skyline',
  },
  'kansas-city': {
    src: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=2000&q=80',
    alt: 'Kansas City skyline',
  },
  vancouver: {
    src: 'https://images.unsplash.com/photo-1547036967-23d11aaca7dc?auto=format&fit=crop&w=2000&q=80',
    alt: 'Vancouver waterfront and mountains',
  },
  toronto: {
    src: 'https://images.unsplash.com/photo-1517935706615-cba718f49e83?auto=format&fit=crop&w=2000&q=80',
    alt: 'Toronto skyline with CN Tower',
  },
  'mexico-city': {
    src: 'https://images.unsplash.com/photo-1518659526055-e7eeece64d0b?auto=format&fit=crop&w=2000&q=80',
    alt: 'Mexico City historic center',
  },
  guadalajara: {
    src: 'https://images.unsplash.com/photo-1596422847843-ef7658a4cdb3?auto=format&fit=crop&w=2000&q=80',
    alt: 'Guadalajara cathedral and plaza',
  },
  monterrey: {
    src: 'https://images.unsplash.com/photo-1512810750469-2667053507b6?auto=format&fit=crop&w=2000&q=80',
    alt: 'Monterrey mountains and city',
  },
};

/** @param {string} guideSlug */
export function itineraryHubSlug(guideSlug) {
  return `${guideSlug}${ITINERARY_HUB_SUFFIX}`;
}

/** @param {string} guideSlug */
export function itineraryHubPath(guideSlug) {
  return `/guides/${itineraryHubSlug(guideSlug)}`;
}

/**
 * @typedef {object} ItineraryHubHost
 * @property {string} guideSlug
 * @property {string} metroLabel
 * @property {string} stadiumVenue
 * @property {string} venueMunicipality
 * @property {string} stadiumSlug API /stadiums/:slug
 * @property {string} country
 * @property {string | null} editorialCityGuideSlug existing longform in articles.js, if any
 */

/** @type {ItineraryHubHost[]} */
export const ITINERARY_HUB_HOSTS = [
  {
    guideSlug: 'new-york',
    metroLabel: 'New York / New Jersey',
    stadiumVenue: 'MetLife Stadium',
    venueMunicipality: 'East Rutherford',
    stadiumSlug: 'metlife-stadium',
    country: 'USA',
    editorialCityGuideSlug: 'new-york-world-cup-2026',
  },
  {
    guideSlug: 'los-angeles',
    metroLabel: 'Los Angeles',
    stadiumVenue: 'SoFi Stadium',
    venueMunicipality: 'Los Angeles',
    stadiumSlug: 'sofi-stadium',
    country: 'USA',
    editorialCityGuideSlug: 'los-angeles-world-cup-2026',
  },
  {
    guideSlug: 'dallas',
    metroLabel: 'Dallas–Fort Worth',
    stadiumVenue: 'AT&T Stadium',
    venueMunicipality: 'Arlington',
    stadiumSlug: 'att-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'atlanta',
    metroLabel: 'Atlanta',
    stadiumVenue: 'Mercedes-Benz Stadium',
    venueMunicipality: 'Atlanta',
    stadiumSlug: 'mercedes-benz-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'seattle',
    metroLabel: 'Seattle',
    stadiumVenue: 'Lumen Field',
    venueMunicipality: 'Seattle',
    stadiumSlug: 'lumen-field',
    country: 'USA',
    editorialCityGuideSlug: 'seattle-world-cup-2026',
  },
  {
    guideSlug: 'san-francisco',
    metroLabel: 'San Francisco Bay Area',
    stadiumVenue: "Levi's Stadium",
    venueMunicipality: 'Santa Clara',
    stadiumSlug: 'levis-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'miami',
    metroLabel: 'Miami',
    stadiumVenue: 'Hard Rock Stadium',
    venueMunicipality: 'Miami',
    stadiumSlug: 'hard-rock-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'houston',
    metroLabel: 'Houston',
    stadiumVenue: 'NRG Stadium',
    venueMunicipality: 'Houston',
    stadiumSlug: 'nrg-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'philadelphia',
    metroLabel: 'Philadelphia',
    stadiumVenue: 'Lincoln Financial Field',
    venueMunicipality: 'Philadelphia',
    stadiumSlug: 'lincoln-financial-field',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'boston',
    metroLabel: 'Boston / Foxborough',
    stadiumVenue: 'Gillette Stadium',
    venueMunicipality: 'Foxborough',
    stadiumSlug: 'gillette-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'kansas-city',
    metroLabel: 'Kansas City',
    stadiumVenue: 'Arrowhead Stadium',
    venueMunicipality: 'Kansas City',
    stadiumSlug: 'arrowhead-stadium',
    country: 'USA',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'vancouver',
    metroLabel: 'Vancouver',
    stadiumVenue: 'BC Place',
    venueMunicipality: 'Vancouver',
    stadiumSlug: 'bc-place',
    country: 'Canada',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'toronto',
    metroLabel: 'Toronto',
    stadiumVenue: 'BMO Field',
    venueMunicipality: 'Toronto',
    stadiumSlug: 'bmo-field',
    country: 'Canada',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'mexico-city',
    metroLabel: 'Mexico City',
    stadiumVenue: 'Estadio Azteca',
    venueMunicipality: 'Mexico City',
    stadiumSlug: 'estadio-azteca',
    country: 'Mexico',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'guadalajara',
    metroLabel: 'Guadalajara',
    stadiumVenue: 'Estadio Akron',
    venueMunicipality: 'Guadalajara',
    stadiumSlug: 'estadio-akron',
    country: 'Mexico',
    editorialCityGuideSlug: null,
  },
  {
    guideSlug: 'monterrey',
    metroLabel: 'Monterrey',
    stadiumVenue: 'Estadio BBVA',
    venueMunicipality: 'Monterrey',
    stadiumSlug: 'estadio-bbva',
    country: 'Mexico',
    editorialCityGuideSlug: null,
  },
];
